import React from "react";
import { Hero, PageContent } from "../../components";

// TODO: Ne pas oublier les unitées + les titres :) (des %)
function Safety() {
  return (
    <div className="flex overflow-x-hidden overflow-y-hidden relative flex-col z-8">
      <Hero title={"Safety"}>
        'Tomorrow: your reward for working {" "}
        <span className="text-lg font-black text-RougePastel md:text-xl lg:text-2xl">
          safely
        </span>{" "}
        today.' - Robert Peltonand
      </Hero>
      <PageContent>
        {`
## Context

Clean Heat is a project aimed at generating heat while safely converting alpha-17-ethinylestradiol into a degradable compound. Utilizing *C. reinhardtii* and *S. meliloti* microorganisms, we prioritize safety to ensure they pose no threats to human health, the environment, or biodiversity. However, being genetically modified organisms (GMOs), stringent safety measures are imperative to minimize any potential risks associated with their growth and proliferation in natural settings.

The development and testing of Clean Heat occurred within SupBiotech Paris, our school lab, which adheres to stringent protocols under EU regulation NF EN 12128. Our lab operates with meticulous attention to safety standards. It remains inaccessible to the public, employing restricted access through a security system. All individuals entering the lab undergo comprehensive training on microorganism handling and lab safety protocols. Additionally, we've established specific rules beyond the basics to further mitigate risks and prevent accidents. Work with our microorganisms takes place in a level 1 security lab, employing biosafety cabinets, chemical hoods, and incubators to ensure sterile conditions and prevent any potential release of the microorganisms.

## Laboratory Rules

- Due to our small lab size, ensure that the number of occupants does not exceed four at any given time.

- Enter the lab wearing a white lab coat and remove it before leaving. This precaution prevents potential contamination inside the lab and reduces the risk of introducing external microorganisms that could affect our culture media.

- Wear gloves consistently during all manipulations to protect against contamination and ensure personal safety.

- Before introducing items into the biosafety cabinet, thoroughly rinse them with ethanol to prevent cross-contamination.

- When handling BET (presumably a substance or equipment), refrain from touching anything that has been in contact with it without wearing gloves. Discard gloves used during BET manipulation in the DASRI (waste) bin once finished.

- When utilizing the UV table, wear a UV protection face mask for safety precautions.

- Use the chemical hood specifically for preparing electrophoresis gels and sample analysis to prevent inhaling any fumes during these processes.

- Clean the biosafety cabinet with ethanol daily and each time microorganisms are changed to prevent cross-contamination.

## Equipment Cleaning:

We possess a few equipment, and we must clean it appropriately in order to have good results and reduce contamination risks.

**Incubator:** The incubator consists of a heating system with multiple floors. Thanks to that we can use it for both bacterial and microalgal growth. The incubator is cleaned each month, by removing what is inside.

**PSM:** We use the PSM every time we want to manipulate our microorganisms in their liquid culture media and mostly to keep them sterile. So, every day, when beginning and ending the day in the lab, we are thoroughly cleaning it with ethanol on each side of the wall and the ground. If we are changing between two organisms, we clean the PSM again to prevent cross-contamination.

**The benches:** We disinfect them each day by cleaning them with ethanol before leaving the lab.

## Our Microorganisms

While working on our project, we used different kinds of microorganisms:

- *Escherichia coli* strain DH5-alpha

- *Sinorhizobium meliloti* strain 1021

- *Chlamydomonas reinhardtii* strain t222+

- Mutant *Chlamydomonas reinhardtii*: LMJ.RY0402.138246, LMJ.RY0402.169159, cw15

We used *E. coli* with the strain DH5-alpha to produce a DNA bank. We used a strain that is non-pathogenic and does not produce any compound harmful to human health.

We used *Sinorhizobium meliloti* strain 1021 for the project. We modified it genetically in order to degrade EE2 in water. The bacterium is non-pathogenic, and the genetic modifications do not change the severity of the bacteria towards human health.

We used *Chlamydomonas reinhardtii* strain t222+ to produce heat. We also used different mutant *Chlamydomonas reinhardtii* to test the production of heat since they were knocked out for their natural AOX production. Our microalgae are non-pathogenic, and genetic modifications do not change the severity of the microalgae towards human health.

## Risk Management

We have imagined a physical and biological barrier to make sure the genetically modified microorganism could not escape the system when implemented ([link to Implementation](https://2023.igem.wiki/ionis-paris/project/implementation)).

- **MICROFILTER MEMBRANE:** Inside our photobioreactor, we want to include a microfilter membrane that will be able to stop the bacteria and microalgae from leaving the photobioreactor in the first place.

- **KILL SWITCH:** We want to implement a kill switch for genetically modified *C. reinhardtii* in case it leaves the reactor. We will use a UV light kill switch to prevent our microalgae from living freely in the nature, in the same way as the iGEM team SORBONNE 2020.

<div class="image_frame">
    <img class="illustration medium" src="https://static.igem.wiki/teams/4577/wiki/safety/sch-ma-microalgue-kill-switch.png">
</div>

The same goes for *S. meliloti* since it is genetically modified. Bacteria being sensitive to UV lights, we planned to put UV lights at the output of our bioreactor to kill incoming bacteria. Just in case, we can implement a UV light kill switch used by AshesiGhana 2022 iGEM team. Originally for *E. coli*, we can codon optimize it and implement it with for *S. meliloti*.

<div class="image_frame">
    <img class="illustration big" src="https://static.igem.wiki/teams/4577/wiki/safety/kill-switch-bact-rie.png">
</div>

For both our microalgae and bacteria, the lights used in the photobioreactor are not composed of UV lights, thus will not induce self-destruction of our microorganisms.

## In the Future

Clean Heat is a bioreactor with different microorganisms. Only professionals should manipulate or enter in contact with the photobioreactor. Should it be the maintenance, repairs, or checking out of the reactor, only authorized people could interact with it in order to drastically reduce the risk of accidental release of genetically modified microorganisms by Clean Heat in the environment.

## Chemical Compound

During our experimentation, we used several chemicals which could represent a danger. To manipulate those chemicals, we used a lab coat, closed shoes, gloves, long pants, and security glasses. Some of them were used inside a chemical flow hood. Below is the list of hazardous chemicals we used during the iGEM competition with an explanation for why we used them.

- **Ethidium Bromide (BET):** Our team used Ethidium Bromide (BET), a CMR agent, to reveal DNA in gels. It is labeled as a cancerogenic compound (H302, H330, H341). While manipulating, risks are that it has chances to be inhaled, ingested or touched accidentally, making it possible to induce genetic anomalies.

<div class="image_frame horizontal">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-toxic.png">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-health-hazard.png">
</div>

- **Bleach:** We used bleach to clean used material and consumables that have been in contact with bacteria and microalgae in order to avoid contamination. It is labeled as corrosive and hazardous to the environment. In contact with bleach, it can

<div class="image_frame horizontal">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-corrosive.png">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-environmental-hazard.jpg">
</div>

- **Ethanol:** Ethanol is used to clean material before and after use, to clean the biosafety cabinet and to clean the incubator. It is labelled as highly flammable and can cause damage to skin if in too much contact. The fumes and the liquid forms are highly flammable.

<div class="image_frame">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-flamable.png">
</div>

- **Ampicillin:** We used Ampicillin to assess the bacteria transformation. It is labeled as a serious health hazard. It can cause cutaneous allergies, allergic symptoms such as asthma or respiratory difficulties via inhalation. It becomes toxic via oral exposition with a concentration superior to 5 mg/kg. 

<div class="image_frame">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-health-hazard.png">
</div>

- **Kanamycin:** We used Kanamycin to assess the bacteria transformation. It is labeled as a serious health hazard. It can cause cutaneous allergies, allergic symptoms such as asthma or respiratory difficulties via inhalation. It can harm the fertility and the fetus.  It becomes toxic via oral exposition with a concentration superior to 4 mg/kg. 

<div class="image_frame">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-health-hazard.png">
</div>

- **Hygromycin:** We used Hygromycin to assess avoid contamination in a culture *C. reinhardtii*. It is labeled as a serious health hazard. It can be fatal if swallowed, in contact with skin or if inhaled. It can cause serious eye damage. It may cause allergy or asthma symptoms or breathing difficulties if inhaled. It becomes toxic via oral exposition with a concentration superior to 5 mg/kg and via inhalation with a concentration superior to 0,00222 mg/kg.  

<div class="image_frame">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-health-hazard.png">
</div>

- **Streptomycine:** We used Streptomycine to assess the bacteria transformation. It is labeled as a serious health hazard. It is suspected of causing cancer and of damaging fertility or the unborn child.  It becomes toxic via oral exposition with a concentration superior to 430 mg/kg.

- **Spectinomycin:** We used Spectinomycin to assess the bacteria transformation. It is labeled as a serious health hazard.  It may cause an allergic skin reaction, asthma symptoms or breathing difficulties if inhaled.  It becomes toxic via oral exposition with a concentration superior to 5 mg/kg.

<div class="image_frame">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-health-hazard.png">
</div>

- **Calcium chloride:** We used Calcium chloride in our culture media. It is labeled as a health hazard. It can cause acute irritation of the eyes.

<div class="image_frame">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-health-hazard.png">
</div>

- **Isopropanol:** We used Isopropanol to precipitate DNA and for gel purification. We aimed to retrieve *C. reinhardtii* DNA. It is labeled as a health hazard and flammable. It can cause irritation of the eyes and can provoke drowsiness or dizziness.

<div class="image_frame">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-health-hazard.png">
</div>

- **TAE 50X:** Our team used a prepared solution of TAE 50X to make a solution of TAE 1X for filling the tank for electrophoresis. TAE is known to be a CMR reagent. It is labeled a health hazard. It can cause cutaneous irritation, a cutaneous allergic reaction, and an acute irritation of the eyes. It is harmful for aquatic organisms.

<div class="image_frame">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-health-hazard.png">
</div>

- **EDTA:** We used EDTA to perform PCR on *C.reinhardtii* colony. It is labelled as a health hazard; it is toxic for aquatic organisms and is hardly biodegradable. It can also cause irritation.  

<div class="image_frame">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-health-hazard.png">
</div>

- **Heavy Metals:** We used heavy metals to create our culture media. Heavy metals are referred to as health hazards and can create different kinds of pulmonary problems if inhaled.

<div class="image_frame">
    <img class="illustration icon" src="https://static.igem.wiki/teams/4577/wiki/safety/white-health-hazard.png">
</div>

## Waste Management

The waste was strictly sorted out to reduce our environmental impact. Every compound that has been or might have been in contact with microorganisms would directly be thrown inside the DASRI trash bin. When we used cones or tools that was in contact with our culture media, we put them in bleach to kill all microorganisms.

        `}
      </PageContent>
    </div>
  );
}

export default Safety;
